import type { JSX } from 'react';
import type { RouteObject } from 'react-router';
import { Navigate } from 'react-router';

import { adminPath, isAdminHost } from '@/shared/config';

import { AdminGuard } from './AdminGuard';
import { ClientLayout } from './ClientLayout';
import { NotFoundRoute } from './NotFoundRoute';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { recoverFromStaleBuild } from './staleBuild';

/**
 * Route-level composition only; pages compose widgets, widgets compose
 * features (CLAUDE.md §7.1). Each route is lazy so the initial bundle stays
 * inside the 200KB budget (CLAUDE.md §7.6) — a patient never downloads the
 * admin panel.
 *
 * The table is exported separately from the browser router so a test can match
 * addresses against it without a DOM.
 *
 * Two hostnames, one build (see `shared/config/hosts`). On the panel's own host
 * only the panel is mounted, at the root — the patient app is not merely hidden
 * there, it has no addresses. On every other origin both tables mount and the
 * panel keeps its `/admin` prefix, which is what a single dev server needs.
 */
type LazyRoute = () => Promise<{ Component: () => JSX.Element }>;

const bookingPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { BookingPage } = await import('@/pages/booking');
  return { Component: BookingPage };
});

const myAppointmentsPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { MyAppointmentsPage } = await import('@/pages/my-appointments');
  return { Component: MyAppointmentsPage };
});

/** «پیام‌ها» — notices from the clinic to the signed-in patient. */
const myMessagesPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { MyMessagesPage } = await import('@/pages/my-messages');
  return { Component: MyMessagesPage };
});

/** «مشاوره تلفنی» — the booking page, on the consultation calendar. */
const consultOnlinePage: LazyRoute = recoverFromStaleBuild(async () => {
  const { BookingPage } = await import('@/pages/booking');
  return {
    Component: function ConsultOnlinePage(): JSX.Element {
      return <BookingPage service="consultation" />;
    },
  };
});

/** «مشاوره» — بررسی آزمایش and the phone consultation. */
const consultPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { ConsultPage } = await import('@/pages/consult');
  return { Component: ConsultPage };
});

const accountPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { AccountPage } = await import('@/pages/account');
  return { Component: AccountPage };
});

/** Where the payment gateway returns the patient (CLAUDE.md §3.5). */
const paymentResultPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { PaymentResultPage } = await import('@/pages/payment-result');
  return { Component: PaymentResultPage };
});

/**
 * Where a scanned receipt QR lands. Short address on purpose: it is printed on
 * paper and read aloud over the phone (CLAUDE.md §3.8).
 */
const receiptPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { ReceiptPage } = await import('@/pages/receipt');
  return { Component: ReceiptPage };
});

const adminLoginPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { AdminLoginPage } = await import('@/pages/admin-login');
  return { Component: AdminLoginPage };
});

/**
 * «نوبت‌ها» — the panel's landing address, and the screen the clinic works
 * from: a day or a window of appointments, filtered, and exportable as a file.
 */
const adminAppointmentsPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { AdminAppointmentsPage } = await import('@/pages/admin-appointments');
  return { Component: AdminAppointmentsPage };
});

/**
 * «آخرین نوبت‌ها» — the newest bookings, whatever day they are for. Its own
 * address rather than an ordering of «نوبت‌ها», so that asking for the latest
 * bookings can never be confused with reordering a single day (see the page).
 */
const adminLatestAppointmentsPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { AdminLatestAppointmentsPage } = await import('@/pages/admin-latest-appointments');
  return { Component: AdminLatestAppointmentsPage };
});

/**
 * «صف بیماران» — today's arrivals, for the دستیار writing نسخه. Its own address
 * because it is a screen kept open all session, not a card on a dashboard.
 */
const adminQueuePage: LazyRoute = recoverFromStaleBuild(async () => {
  const { AdminQueuePage } = await import('@/pages/admin-queue');
  return { Component: AdminQueuePage };
});

/**
 * «مشاوره‌های امروز» — the doctor's phone consultations: dial, count the
 * interval down, record how it ended. Its own address for the same reason as
 * the queue: it is worked from, not glanced at.
 */
const adminConsultationsPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { AdminConsultationsPage } = await import('@/pages/admin-consultations');
  return { Component: AdminConsultationsPage };
});

/** «بررسی آزمایش» — the lab results waiting on the doctor. */
const adminLabReviewsPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { AdminLabReviewsPage } = await import('@/pages/admin-lab-reviews');
  return { Component: AdminLabReviewsPage };
});

/**
 * The داشبورد — how full the days are, against the capacity that was opened.
 * A weekly question rather than an hourly one, so it sits behind `/reports`
 * while the appointments themselves hold the landing address.
 */
const adminDashboardPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { AdminDashboardPage } = await import('@/pages/admin-dashboard');
  return { Component: AdminDashboardPage };
});

/** وقت‌دهی — the schedule, its overrides, the policy and the price. */
const adminSchedulePage: LazyRoute = recoverFromStaleBuild(async () => {
  const { AdminSchedulePage } = await import('@/pages/admin-schedule');
  return { Component: AdminSchedulePage };
});

/**
 * «تنظیمات» — what the clinic *is*: the آدرس مطب a رسید prints and the map link
 * its «مسیریابی» QR opens. Its own address rather than a tab inside وقت‌دهی,
 * which is about *when* the clinic works.
 */
const adminSettingsPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { AdminSettingsPage } = await import('@/pages/admin-settings');
  return { Component: AdminSettingsPage };
});

/**
 * «پرداخت‌ها» — the money log. Its own address rather than a card on the
 * داشبورد: it is opened to answer a question about one payment, and an operator
 * who is asked «پول من کجا رفت؟» on the phone needs to land on it directly.
 */
const adminPaymentsPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { AdminPaymentsPage } = await import('@/pages/admin-payments');
  return { Component: AdminPaymentsPage };
});

/**
 * «مدیریت سایت» — the public landing page's content. `/site`, not `/landing`
 * or `/`: the panel's root is «نوبت‌ها», and the address names what it edits.
 */
const adminLandingPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { AdminLandingPage } = await import('@/pages/admin-landing');
  return { Component: AdminLandingPage };
});

/** «پیش‌نمایش» — the website's saved draft, rendered before it is published. */
const adminLandingPreviewPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { AdminLandingPreviewPage } = await import('@/pages/admin-landing-preview');
  return { Component: AdminLandingPreviewPage };
});

/** The access-control section: roles, permissions, and who holds them. */
const adminAccessPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { AdminAccessPage } = await import('@/pages/admin-access');
  return { Component: AdminAccessPage };
});

/** «پیام‌ها» — sending, and «پیگیری‌های بی‌پاسخ». `/follow-ups`, not `/records`: on the panel's own host
 * that would collide with the patient app's پرونده tab. */
const adminRecordsPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { AdminRecordsPage } = await import('@/pages/admin-records');
  return { Component: AdminRecordsPage };
});

/** «گفتگو» — staff writing to each other: text, files and voice notes. */
const adminChatPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { AdminChatPage } = await import('@/pages/admin-chat');
  return { Component: AdminChatPage };
});

/** The کاربران section: patient accounts and staff, blocked or let back in. */
const adminUsersPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { AdminUsersPage } = await import('@/pages/admin-users');
  return { Component: AdminUsersPage };
});

/** One account from that list: their مشخصات and their نوبت‌ها, at their own
 * address — so a patient can be bookmarked and the back button returns to the
 * search that was already typed. */
const adminAccountPage: LazyRoute = recoverFromStaleBuild(async () => {
  const { AdminAccountPage } = await import('@/pages/admin-account');
  return { Component: AdminAccountPage };
});

/**
 * «پروفایل من» — the signed-in person's own name, «عنوان» and password. Not a
 * navigation section: it is reached from the name in the panel's chrome, and
 * the section list is capped at what fits a phone's bottom edge.
 */
const adminProfilePage: LazyRoute = recoverFromStaleBuild(async () => {
  const { AdminProfilePage } = await import('@/pages/admin-profile');
  return { Component: AdminProfilePage };
});

const patientRoutes: readonly RouteObject[] = [
  // The four patient tabs share one shell, so the header and the tab bar are
  // mounted once and survive every navigation between them.
  {
    Component: ClientLayout,
    ErrorBoundary: RouteErrorBoundary,
    children: [
      { index: true, lazy: bookingPage },
      { path: 'appointments', lazy: myAppointmentsPage },
      { path: 'messages', lazy: myMessagesPage },
      { path: 'consult', lazy: consultPage },
      { path: 'consult/online', lazy: consultOnlinePage },
      // پرونده is hidden for now: the tab is gone from the bar, and the address
      // sends anyone holding an old link back to booking rather than to a page
      // that is not meant to be seen yet. The page itself is untouched under
      // `pages/records` — restoring it is this line plus the tab entry.
      { path: 'records', element: <Navigate replace to="/" /> },
      { path: 'account', lazy: accountPage },
      // inside the patient shell on purpose: coming back from the bank should
      // land somewhere that still looks like the app, tab bar and all
      { path: 'payment/result', lazy: paymentResultPage },
      // A receipt is opened by people who are not signed in — the clinic's desk
      // scanning a phone, or a patient on a device that signed out — so it sits
      // in the shell like any other patient page, gated by nothing but the code.
      { path: 'r/:code', lazy: receiptPage },
      // The same page with no code: where the desk types one in when the
      // scanner will not read the phone in front of it.
      { path: 'r', lazy: receiptPage },
    ],
  },
  // Everything the tables above do not claim. Last on purpose: on a shared
  // origin the panel's own routes are appended after this list and must still
  // match, so the catch-all is added once, at the very end (see below).
];

const adminRoutes: readonly RouteObject[] = [
  { path: adminPath('/login'), lazy: adminLoginPage, ErrorBoundary: RouteErrorBoundary },
  {
    path: adminPath(),
    Component: AdminGuard,
    ErrorBoundary: RouteErrorBoundary,
    children: [
      // The panel's landing address is the one the clinic types and bookmarks.
      // Without an index route the guard rendered an empty `<Outlet />` — a
      // blank white page for a signed-in admin, which reads as "the panel is
      // broken". «نوبت‌ها» renders here directly rather than redirecting, so the
      // landing address costs no extra navigation. It is the screen the morning
      // is spent on, and it needs only `appointments.view` — the widest
      // permission in the panel, so it is the section most staff can open.
      //
      // Deliberately *not* also mounted at `/appointments`: on the panel's own
      // hostname that address belongs to the patient app's «نوبت‌های من» and
      // must keep falling through to the catch-all.
      { index: true, lazy: adminAppointmentsPage },
      { path: 'queue', lazy: adminQueuePage },
      { path: 'consultations', lazy: adminConsultationsPage },
      { path: 'lab-reviews', lazy: adminLabReviewsPage },
      { path: 'latest', lazy: adminLatestAppointmentsPage },
      { path: 'reports', lazy: adminDashboardPage },
      { path: 'schedule', lazy: adminSchedulePage },
      { path: 'payments', lazy: adminPaymentsPage },
      { path: 'access', lazy: adminAccessPage },
      { path: 'users', lazy: adminUsersPage },
      { path: 'users/:accountId', lazy: adminAccountPage },
      { path: 'follow-ups', lazy: adminRecordsPage },
      { path: 'chat', lazy: adminChatPage },
      { path: 'settings', lazy: adminSettingsPage },
      { path: 'site', lazy: adminLandingPage },
      { path: 'site/preview', lazy: adminLandingPreviewPage },
      { path: 'profile', lazy: adminProfilePage },
      // Anything else under the panel belongs to the panel too; send it to the
      // landing address rather than to React Router's default 404.
      { path: '*', element: <Navigate replace to={adminPath()} /> },
    ],
  },
];

/**
 * An address neither table claims — a mistyped link, a bookmark from a version
 * that had a page here. It renders the same screen every other dead end does
 * rather than React Router's developer 404.
 */
const notFoundRoute: RouteObject = { path: '*', Component: NotFoundRoute };

/**
 * The catch-all is appended only off the panel's own host. There the panel is
 * mounted at the root and already claims every address under it, sending a
 * stray one to «نوبت‌ها» — the better answer for staff, who are always signed
 * in and always meant to be somewhere in the panel.
 */
export const routes: readonly RouteObject[] = isAdminHost()
  ? adminRoutes
  : [...patientRoutes, ...adminRoutes, notFoundRoute];
